import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Package, Ticket, Layers, Save, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { canAccessCentroDiurno } from "../../utils/centroDiurnoAccess";
import {
  getPacchettiConfig,
  savePacchettiConfig,
  getCarnetOspiti,
  updateCarnetOspite,
} from "../../utils/centroDiurnoApi";
import PageHeader from "../../components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function slugify(text) {
  return String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 48);
}

const STATO_CARNET = {
  ok: { label: "OK", className: "bg-emerald-100 text-emerald-800" },
  attenzione: { label: "In scadenza", className: "bg-amber-100 text-amber-800" },
  esaurito: { label: "Esaurito", className: "bg-red-100 text-red-800" },
  scaduto: { label: "Scaduto", className: "bg-slate-200 text-slate-700" },
};

function PacchettoOptionToggle({ label, description, checked, onCheckedChange, disabled }) {
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 transition-colors ${
        checked ? "bg-emerald-50/70 border-emerald-200" : "bg-slate-50 border-slate-200"
      }`}
    >
      <div className="min-w-0">
        <p className={`text-sm font-medium leading-tight ${checked ? "text-emerald-900" : "text-slate-700"}`}>
          {label}
        </p>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="shrink-0 data-[state=checked]:bg-emerald-600"
      />
    </div>
  );
}

export default function CentroDiurnoPacchetti() {
  const { token, user, hasPermission, activeStruttura } = useAuth();
  const canManage = hasPermission("centro_diurno:manage");
  const [servizi, setServizi] = useState([]);
  const [pacchetti, setPacchetti] = useState([]);
  const [carnet, setCarnet] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editCarnet, setEditCarnet] = useState(null);
  const [carnetForm, setCarnetForm] = useState({ carnet_residui: 0, carnet_scadenza: "" });

  const loadConfig = useCallback(async () => {
    if (!token) return;
    const cfg = await getPacchettiConfig(token);
    setServizi(cfg.servizi || []);
    setPacchetti(cfg.pacchetti || []);
  }, [token, activeStruttura?.id]);

  const loadCarnet = useCallback(async () => {
    if (!token) return;
    const res = await getCarnetOspiti(token);
    setCarnet(res.carnet || []);
  }, [token, activeStruttura?.id]);

  const load = useCallback(async () => {
    try {
      await Promise.all([loadConfig(), loadCarnet()]);
    } catch (e) {
      toast.error("Errore caricamento configurazione");
    }
  }, [loadConfig, loadCarnet]);

  useEffect(() => {
    if (canAccessCentroDiurno(user)) load();
  }, [load, user]);

  const serviziAttivi = useMemo(
    () => servizi.filter((s) => s.attivo !== false),
    [servizi]
  );

  const handleSaveConfig = async () => {
    if (!canManage) return;
    setSaving(true);
    try {
      await savePacchettiConfig(token, { servizi, pacchetti });
      toast.success("Configurazione salvata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const addServizio = () => {
    const nome = "Nuovo servizio";
    let id = slugify(nome);
    const existing = new Set(servizi.map((s) => s.id));
    let n = 1;
    while (existing.has(id)) {
      id = `${slugify(nome)}_${n++}`;
    }
    setServizi([...servizi, { id, nome, attivo: true }]);
  };

  const updateServizio = (index, field, value) => {
    setServizi((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const removeServizio = (index) => {
    const sid = servizi[index]?.id;
    const used = pacchetti.some((p) => (p.servizi_ids || []).includes(sid));
    if (used) {
      toast.error("Servizio usato in un pacchetto: rimuovilo dal pacchetto prima");
      return;
    }
    setServizi((prev) => prev.filter((_, i) => i !== index));
  };

  const addPacchetto = () => {
    const nome = "Nuovo pacchetto";
    let id = slugify(nome);
    const existing = new Set(pacchetti.map((p) => p.id));
    let n = 1;
    while (existing.has(id)) {
      id = `${slugify(nome)}_${n++}`;
    }
    setPacchetti([
      ...pacchetti,
      {
        id,
        nome,
        descrizione: "",
        servizi_ids: [],
        is_carnet: false,
        carnet_ingressi: 10,
        carnet_giorni_validita: 60,
        trasporto_ar_default: false,
        attivo: true,
      },
    ]);
  };

  const updatePacchetto = (index, field, value) => {
    setPacchetti((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const toggleServizioPacchetto = (pIndex, servizioId) => {
    setPacchetti((prev) =>
      prev.map((p, i) => {
        if (i !== pIndex) return p;
        const ids = new Set(p.servizi_ids || []);
        if (ids.has(servizioId)) ids.delete(servizioId);
        else ids.add(servizioId);
        return { ...p, servizi_ids: [...ids] };
      })
    );
  };

  const removePacchetto = (index) => {
    if (pacchetti.length <= 1) {
      toast.error("Deve restare almeno un pacchetto");
      return;
    }
    setPacchetti((prev) => prev.filter((_, i) => i !== index));
  };

  const openEditCarnet = (item) => {
    setEditCarnet(item);
    setCarnetForm({
      carnet_residui: item.carnet_residui ?? 0,
      carnet_scadenza: item.carnet_scadenza || "",
    });
  };

  const saveCarnetOspite = async () => {
    if (!editCarnet) return;
    try {
      await updateCarnetOspite(token, editCarnet.fascicolo_id, carnetForm);
      toast.success("Carnet aggiornato");
      setEditCarnet(null);
      loadCarnet();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento carnet");
    }
  };

  if (!canAccessCentroDiurno(user)) {
    return <div className="p-8 text-center text-slate-600">Modulo non attivo</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
        <PageHeader
          title="Pacchetti e Servizi"
          subtitle="Definisci i pacchetti del centro diurno e monitora i carnet degli ospiti"
          icon={Package}
          iconContainerClassName="bg-gradient-to-br from-emerald-500 to-emerald-700"
          iconClassName="text-white"
        />

        <Tabs defaultValue="pacchetti">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="pacchetti" className="gap-1.5">
              <Package className="w-4 h-4" /> Pacchetti
            </TabsTrigger>
            <TabsTrigger value="servizi" className="gap-1.5">
              <Layers className="w-4 h-4" /> Servizi
            </TabsTrigger>
            <TabsTrigger value="carnet" className="gap-1.5">
              <Ticket className="w-4 h-4" /> Carnet ospiti
              {carnet.filter((c) => c.stato !== "ok").length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px] bg-amber-100 text-amber-800">
                  {carnet.filter((c) => c.stato !== "ok").length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="servizi" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base">Catalogo servizi</CardTitle>
                {canManage && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={addServizio}>
                      <Plus className="w-4 h-4 mr-1" /> Aggiungi servizio
                    </Button>
                    <Button size="sm" className="bg-[#4CAF50] hover:bg-[#43A047]" onClick={handleSaveConfig} disabled={saving}>
                      <Save className="w-4 h-4 mr-1" /> Salva
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-500">
                  Elenco dei servizi che puoi associare ai pacchetti (es. pasto, farmaci, trasporto).
                </p>
                {servizi.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">Nessun servizio configurato</p>
                ) : (
                  <div className="rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
                    {servizi.map((s, i) => {
                      const attivo = s.attivo !== false;
                      return (
                        <div
                          key={s.id}
                          className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                            attivo ? "bg-white" : "bg-slate-50/80"
                          }`}
                        >
                          <Input
                            value={s.nome}
                            onChange={(e) => updateServizio(i, "nome", e.target.value)}
                            disabled={!canManage}
                            className={`flex-1 min-w-0 border-slate-200 ${
                              !attivo ? "text-slate-500" : ""
                            }`}
                          />
                          <div
                            className={`flex items-center gap-2.5 shrink-0 pl-3 pr-1 py-1 rounded-full border transition-colors ${
                              attivo
                                ? "bg-emerald-50 border-emerald-200"
                                : "bg-slate-100 border-slate-200"
                            }`}
                          >
                            <span
                              className={`text-xs font-medium select-none ${
                                attivo ? "text-emerald-700" : "text-slate-500"
                              }`}
                            >
                              {attivo ? "Attivo" : "Disattivo"}
                            </span>
                            <Switch
                              checked={attivo}
                              disabled={!canManage}
                              onCheckedChange={(v) => updateServizio(i, "attivo", v)}
                              className="data-[state=checked]:bg-emerald-600"
                            />
                          </div>
                          {canManage && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                              onClick={() => removeServizio(i)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pacchetti" className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-2 justify-between items-center">
              <p className="text-sm text-slate-600">
                Ogni pacchetto definisce i servizi inclusi. I pacchetti Carnet scalano ingressi al check-in.
              </p>
              {canManage && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={addPacchetto}>
                    <Plus className="w-4 h-4 mr-1" /> Nuovo pacchetto
                  </Button>
                  <Button size="sm" className="bg-[#4CAF50] hover:bg-[#43A047]" onClick={handleSaveConfig} disabled={saving}>
                    <Save className="w-4 h-4 mr-1" /> Salva configurazione
                  </Button>
                </div>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {pacchetti.map((p, pIndex) => (
                <Card key={p.id} className={p.attivo === false ? "opacity-60" : ""}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 space-y-2">
                        <Input
                          value={p.nome}
                          onChange={(e) => updatePacchetto(pIndex, "nome", e.target.value)}
                          disabled={!canManage}
                          className="font-semibold"
                        />
                        <Textarea
                          value={p.descrizione || ""}
                          onChange={(e) => updatePacchetto(pIndex, "descrizione", e.target.value)}
                          disabled={!canManage}
                          rows={2}
                          placeholder="Descrizione del pacchetto"
                          className="text-sm"
                        />
                      </div>
                      {canManage && pacchetti.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removePacchetto(pIndex)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Servizi inclusi</p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {serviziAttivi.map((s) => {
                          const selected = (p.servizi_ids || []).includes(s.id);
                          return (
                            <label
                              key={s.id}
                              className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${
                                !canManage ? "cursor-default" : "cursor-pointer"
                              } ${
                                selected
                                  ? "bg-emerald-50 border-emerald-200 shadow-sm"
                                  : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                              }`}
                            >
                              <Checkbox
                                checked={selected}
                                disabled={!canManage}
                                onCheckedChange={() => toggleServizioPacchetto(pIndex, s.id)}
                                className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                              />
                              <span className={`text-sm leading-tight ${selected ? "text-emerald-900 font-medium" : "text-slate-700"}`}>
                                {s.nome}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div className="space-y-2 pt-1">
                      <PacchettoOptionToggle
                        label="Pacchetto Carnet"
                        description="Ingressi a scalare al check-in"
                        checked={Boolean(p.is_carnet)}
                        disabled={!canManage}
                        onCheckedChange={(v) => updatePacchetto(pIndex, "is_carnet", v)}
                      />

                      {p.is_carnet && (
                        <div className="grid grid-cols-2 gap-3 pl-1">
                          <div>
                            <Label className="text-xs">N. ingressi</Label>
                            <Input
                              type="number"
                              min={1}
                              value={p.carnet_ingressi ?? 10}
                              disabled={!canManage}
                              onChange={(e) => updatePacchetto(pIndex, "carnet_ingressi", parseInt(e.target.value, 10) || 10)}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Validità (giorni)</Label>
                            <Input
                              type="number"
                              min={1}
                              value={p.carnet_giorni_validita ?? 60}
                              disabled={!canManage}
                              onChange={(e) =>
                                updatePacchetto(pIndex, "carnet_giorni_validita", parseInt(e.target.value, 10) || 60)
                              }
                            />
                          </div>
                        </div>
                      )}

                      <PacchettoOptionToggle
                        label="Trasporto A/R"
                        description="Attivo di default in prenotazione"
                        checked={Boolean(p.trasporto_ar_default)}
                        disabled={!canManage}
                        onCheckedChange={(v) => updatePacchetto(pIndex, "trasporto_ar_default", v)}
                      />

                      <PacchettoOptionToggle
                        label="Disponibile in prenotazione"
                        description="Mostrato nell'agenda al nuovo accesso"
                        checked={p.attivo !== false}
                        disabled={!canManage}
                        onCheckedChange={(v) => updatePacchetto(pIndex, "attivo", v)}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {canManage && (
              <div className="flex justify-end">
                <Button className="bg-[#4CAF50] hover:bg-[#43A047]" onClick={handleSaveConfig} disabled={saving}>
                  <Save className="w-4 h-4 mr-1" /> Salva configurazione
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="carnet" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Stato carnet per ospite</CardTitle>
              </CardHeader>
              <CardContent>
                {carnet.length === 0 ? (
                  <p className="text-sm text-slate-500 py-8 text-center">
                    Nessun ospite con pacchetto Carnet attivo
                  </p>
                ) : (
                  <div className="space-y-3">
                    {carnet.map((c) => {
                      const totali = c.carnet_ingressi_totali || 10;
                      const residui = c.carnet_residui ?? 0;
                      const pct = totali > 0 ? Math.round((residui / totali) * 100) : 0;
                      const stato = STATO_CARNET[c.stato] || STATO_CARNET.ok;
                      const scadenzaLabel = c.carnet_scadenza
                        ? format(parseISO(c.carnet_scadenza), "d MMM yyyy", { locale: it })
                        : "—";
                      return (
                        <div
                          key={c.fascicolo_id}
                          className="p-4 rounded-xl border border-slate-200 bg-white flex flex-wrap gap-4 justify-between items-center"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-slate-800">{c.ospite_nome}</p>
                              <Badge className={stato.className}>{stato.label}</Badge>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{c.pacchetto_nome}</p>
                            <div className="mt-3 max-w-xs">
                              <div className="flex justify-between text-xs text-slate-600 mb-1">
                                <span>{residui} ingressi residui</span>
                                <span>su {totali}</span>
                              </div>
                              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${
                                    pct <= 20 ? "bg-red-500" : pct <= 50 ? "bg-amber-500" : "bg-emerald-500"
                                  }`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                            <p className="text-xs text-slate-500 mt-2">
                              Scadenza: {scadenzaLabel}
                              {c.giorni_alla_scadenza != null && c.giorni_alla_scadenza >= 0 && (
                                <span> · {c.giorni_alla_scadenza} giorni rimanenti</span>
                              )}
                              {c.giorni_alla_scadenza != null && c.giorni_alla_scadenza < 0 && (
                                <span className="text-red-600"> · scaduto</span>
                              )}
                            </p>
                          </div>
                          {canManage && (
                            <Button size="sm" variant="outline" onClick={() => openEditCarnet(c)}>
                              <Pencil className="w-4 h-4 mr-1" /> Aggiorna
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={Boolean(editCarnet)} onOpenChange={(open) => !open && setEditCarnet(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Aggiorna carnet — {editCarnet?.ospite_nome}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Ingressi residui</Label>
                <Input
                  type="number"
                  min={0}
                  value={carnetForm.carnet_residui}
                  onChange={(e) =>
                    setCarnetForm({ ...carnetForm, carnet_residui: parseInt(e.target.value, 10) || 0 })
                  }
                />
              </div>
              <div>
                <Label>Data scadenza</Label>
                <Input
                  type="date"
                  value={carnetForm.carnet_scadenza}
                  onChange={(e) => setCarnetForm({ ...carnetForm, carnet_scadenza: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditCarnet(null)}>
                Annulla
              </Button>
              <Button className="bg-[#4CAF50] hover:bg-[#43A047]" onClick={saveCarnetOspite}>
                Salva
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
